import type { ReactElement } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { EllipsisDropdownItem, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import {
    LockedDropdownItem,
    DropdownOptionText,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/models-selector/ModelSelector.styled";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { ICountryObject } from "app/services/CountryService";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type { ExplicitLLModel } from "../../types";

import { ModelIcon } from "./ModelIcon";

interface IModelDropdownItemProps {
    id: string;
    model: ExplicitLLModel;
    isDisabled: boolean;
    onLockedClick: () => void;
    selectedCountry?: ICountryObject;
    [key: string]: unknown;
}

export const ModelSelectorItem = ({
    id,
    model,
    isDisabled,
    onLockedClick,
    selectedCountry,
    ...dropdownInjectedProps
}: IModelDropdownItemProps) => {
    const isNTUser = isNoTouch(swSettings);
    const isLocked = isNTUser && isDisabled;
    const label = translate(AI_MODELS_CONFIG[model].displayNameKey);

    const renderOptionLabel = (): ReactElement => (
        <>
            <ModelIcon model={model} />
            <DropdownOptionText
                fontVariant="input2"
                color={colorsPalettes.carbon[400]}
            >
                {label}
            </DropdownOptionText>
        </>
    );

    return isLocked ? (
        <LockedDropdownItem
            id={id}
            preventDefault
            {...dropdownInjectedProps}
            onClick={onLockedClick}
        >
            <Flex
                gap="8px"
                alignItems="center"
                margin="0 0 0 15px"
            >
                {renderOptionLabel()}
            </Flex>
        </LockedDropdownItem>
    ) : (
        <Tooltip
            placement="left"
            enabled={isDisabled && !!selectedCountry}
            content={translate("llm.setup.models.disabled-tooltip", {
                country: selectedCountry?.text ?? "",
            })}
        >
            <div>
                <EllipsisDropdownItem
                    id={id}
                    showCheckBox
                    disabled={isDisabled}
                    {...dropdownInjectedProps}
                >
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        {renderOptionLabel()}
                    </Flex>
                </EllipsisDropdownItem>
            </div>
        </Tooltip>
    );
};
